import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useSessionStore } from '@/store/sessionStore'
import { fetchChampionBoard } from '@/api/champion'
import type { ChampionBoardEntry } from '@/api/champion'
import { Spinner } from '@/components/ui/Spinner'
import { LangSwitcher } from '@/components/ui/LangSwitcher'
import { useLocalizedTeamName } from '@/utils/teamNames'

export function ChampionBoardPage() {
  const { t } = useTranslation()
  const getTeamName = useLocalizedTeamName()
  const { playerId } = useSessionStore()
  const [champion, setChampion] = useState<string | null>(null)
  const [picks, setPicks] = useState<ChampionBoardEntry[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    load()
    const id = setInterval(load, 30_000)
    return () => clearInterval(id)
  }, [])

  async function load() {
    try {
      const data = await fetchChampionBoard()
      setChampion(data.champion)
      setPicks(data.picks)
    } finally {
      setLoading(false)
    }
  }

  const byTeam = picks.reduce<Record<string, ChampionBoardEntry[]>>((acc, p) => {
    acc[p.team_id] = [...(acc[p.team_id] ?? []), p]
    return acc
  }, {})

  const teams = Object.values(byTeam).sort((a, b) => {
    const aCorrect = champion && a[0].team_id === champion ? 1 : 0
    const bCorrect = champion && b[0].team_id === champion ? 1 : 0
    if (bCorrect !== aCorrect) return bCorrect - aCorrect
    return b.length - a.length
  })

  const myPick = picks.find(p => p.player_id === playerId)
  const totalPickers = picks.length
  const correctCount = champion ? picks.filter(p => p.team_id === champion).length : 0

  return (
    <div className="min-h-screen">
      <div className="border-b border-white/10 bg-pitch-900/80 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <span className="text-2xl">🎯</span>
            <div>
              <h1 className="text-base font-black text-white leading-tight">{t('championBoard.title')}</h1>
              <p className="text-[11px] text-white/40">{t('championBoard.subtitle')}</p>
            </div>
          </div>
          <nav className="flex gap-2 items-center">
            <LangSwitcher />
            <a href="/champion" className="btn-secondary btn-sm text-sm">🏆 {t('championBoard.myPick')}</a>
            <a href="/bracket" className="btn-secondary btn-sm text-sm">⚽ {t('nav.bracket')}</a>
            <a href="/leaderboard" className="btn-secondary btn-sm text-sm">🏅 {t('nav.leaderboard')}</a>
          </nav>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-6">
        {/* Champion reveal */}
        {champion ? (
          <div className="card p-5 mb-6 text-center border-gold-400/30 bg-gold-400/5">
            <p className="text-xs text-white/40 mb-2 uppercase tracking-widest">{t('championBoard.worldChampion')}</p>
            <div className="text-5xl mb-2">
              {picks.find(p => p.team_id === champion)?.team_flag ?? '🏆'}
            </div>
            <p className="text-xl font-black text-gold-400">
              {getTeamName(champion, picks.find(p => p.team_id === champion)?.team_name ?? champion)}
            </p>
            <p className="text-sm text-white/50 mt-2">
              {t('championBoard.correctOf', { count: correctCount, total: totalPickers, plural: totalPickers !== 1 ? 's' : '' })}
            </p>
          </div>
        ) : (
          <div className="card p-4 mb-6 text-center border-white/10">
            <p className="text-3xl mb-2">⏳</p>
            <p className="text-sm text-white/40">{t('championBoard.pending')}</p>
          </div>
        )}

        {/* My pick */}
        {myPick && (
          <div className={`card p-3 mb-6 flex items-center gap-3 ${
            champion
              ? myPick.team_id === champion
                ? 'border-emerald-500/40 bg-emerald-500/5'
                : 'border-red-500/30 bg-red-500/5'
              : 'border-white/10'
          }`}>
            <span className="text-2xl">{myPick.team_flag ?? '🏳'}</span>
            <div className="flex-1">
              <p className="text-xs text-white/40">{t('championBoard.yourPick')}</p>
              <p className="text-sm font-bold text-white">{getTeamName(myPick.team_id, myPick.team_name)}</p>
            </div>
            {champion && (
              <span className={`text-lg font-black ${myPick.team_id === champion ? 'text-emerald-400' : 'text-red-400'}`}>
                {myPick.team_id === champion ? '✓ +20pts' : '✗'}
              </span>
            )}
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-3 gap-3 mb-6">
          <div className="card p-3 text-center">
            <div className="text-xl font-black text-white">{totalPickers}</div>
            <div className="text-[10px] text-white/40">{t('championBoard.totalPicks')}</div>
          </div>
          <div className="card p-3 text-center">
            <div className="text-xl font-black text-white">{teams.length}</div>
            <div className="text-[10px] text-white/40">{t('championBoard.teamsPicked')}</div>
          </div>
          <div className="card p-3 text-center">
            <div className="text-xl font-black text-emerald-400">{correctCount}</div>
            <div className="text-[10px] text-white/40">{t('championBoard.correct')}</div>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-20"><Spinner size="lg" /></div>
        ) : picks.length === 0 ? (
          <div className="text-center py-20 text-white/40">
            <div className="text-4xl mb-3">🏆</div>
            <p>{t('championBoard.noPicks')}</p>
            <a href="/champion" className="btn-primary btn-sm mt-4 inline-flex">{t('championBoard.pickChampion')}</a>
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            {teams.map(group => {
              const { team_id, team_name, team_flag } = group[0]
              const isChampion = champion && team_id === champion
              const isWrong = champion && team_id !== champion
              return (
                <div key={team_id}
                  className={`card p-4 ${
                    isChampion ? 'border-emerald-500/40 bg-emerald-500/5' :
                    isWrong ? 'border-white/5 opacity-60' : 'border-white/10'
                  }`}
                >
                  <div className="flex items-center gap-3 mb-3">
                    <span className="text-3xl">{team_flag ?? '🏳'}</span>
                    <div className="flex-1">
                      <span className="font-bold text-white">{getTeamName(team_id, team_name)}</span>
                      {isChampion && <span className="ml-2 text-xs text-emerald-400 font-semibold">{t('championBoard.champion')}</span>}
                    </div>
                    <span className="text-sm text-white/50">
                      {t('championBoard.picked', { count: group.length, plural: group.length !== 1 ? 's' : '' })}
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {group.map(p => (
                      <span key={p.player_id}
                        className={`text-xs px-2 py-0.5 rounded-full border ${
                          p.player_id === playerId
                            ? 'bg-gold-400/20 border-gold-400/40 text-gold-400 font-semibold'
                            : 'bg-white/5 border-white/10 text-white/60'
                        }`}
                      >
                        {p.is_ai === 1 ? '🤖 ' : ''}{p.display_name}
                        {isChampion && ' +20'}
                      </span>
                    ))}
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}
